import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, User, X } from 'lucide-react';
import { apiCall } from './invoiceApi';

/**
 * Client Modal
 * Modal for creating/editing clients
 */
const ClientModal = ({ client, language, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: client?.name || '',
    contact_name: client?.contact_name || '',
    email: client?.email || '',
    phone: client?.phone || '',
    vat_number: client?.vat_number || '',
    address: client?.address || '',
    postal_code: client?.postal_code || '',
    city: client?.city || '',
    country: client?.country || 'Belgique',
    notes: client?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const t = language === 'fr' ? {
    title: client ? 'Modifier le client' : 'Nouveau client',
    name: 'Nom / Société *',
    contact: 'Personne de contact',
    email: 'Email',
    phone: 'Téléphone',
    vatNumber: 'N° TVA',
    address: 'Adresse',
    postalCode: 'Code postal',
    city: 'Ville',
    country: 'Pays',
    notes: 'Notes',
    save: 'Enregistrer',
    cancel: 'Annuler',
  } : {
    title: client ? 'Klant bewerken' : 'Nieuwe klant',
    name: 'Naam / Bedrijf *',
    contact: 'Contactpersoon',
    email: 'Email',
    phone: 'Telefoon',
    vatNumber: 'BTW-nummer',
    address: 'Adres',
    postalCode: 'Postcode',
    city: 'Stad',
    country: 'Land',
    notes: 'Opmerkingen',
    save: 'Opslaan',
    cancel: 'Annuleren',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold">{t.title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">{t.name}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t.contact}</label>
              <input
                type="text"
                value={formData.contact_name}
                onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t.vatNumber}</label>
              <input
                type="text"
                value={formData.vat_number}
                onChange={(e) => setFormData(prev => ({ ...prev, vat_number: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="BE0123456789"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t.email}</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t.phone}</label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">{t.address}</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t.postalCode}</label>
              <input
                type="text"
                value={formData.postal_code}
                onChange={(e) => setFormData(prev => ({ ...prev, postal_code: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t.city}</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">{t.notes}</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg">
              {t.cancel}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-amber-700 hover:bg-amber-800 text-white rounded-lg"
            >
              {saving ? '...' : t.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * Clients Tab
 * Tab for managing clients/customers
 */
const ClientsTab = ({ language }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const t = language === 'fr' ? {
    title: 'Gestion des Clients',
    addClient: 'Nouveau client',
    search: 'Rechercher un client...',
    name: 'Nom / Société',
    contact: 'Contact',
    email: 'Email',
    phone: 'Téléphone',
    vatNumber: 'N° TVA',
    address: 'Adresse',
    postalCode: 'Code postal',
    city: 'Ville',
    country: 'Pays',
    notes: 'Notes',
    actions: 'Actions',
    edit: 'Modifier',
    delete: 'Supprimer',
    save: 'Enregistrer',
    cancel: 'Annuler',
    noClients: 'Aucun client enregistré',
    confirmDelete: 'Êtes-vous sûr de vouloir supprimer ce client ?',
    clientSaved: 'Client enregistré',
  } : {
    title: 'Klantenbeheer',
    addClient: 'Nieuwe klant',
    search: 'Zoek een klant...',
    name: 'Naam / Bedrijf',
    contact: 'Contactpersoon',
    email: 'Email',
    phone: 'Telefoon',
    vatNumber: 'BTW-nummer',
    address: 'Adres',
    postalCode: 'Postcode',
    city: 'Stad',
    country: 'Land',
    notes: 'Opmerkingen',
    actions: 'Acties',
    edit: 'Bewerken',
    delete: 'Verwijderen',
    save: 'Opslaan',
    cancel: 'Annuleren',
    noClients: 'Geen klanten geregistreerd',
    confirmDelete: 'Weet u zeker dat u deze klant wilt verwijderen?',
    clientSaved: 'Klant opgeslagen',
  };

  const loadClients = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/api/clients');
      setClients(response.clients || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  const handleSave = async (clientData) => {
    try {
      if (editingClient) {
        await apiCall(`/api/clients/${editingClient.id}`, {
          method: 'PUT',
          body: JSON.stringify(clientData),
        });
      } else {
        await apiCall('/api/clients', {
          method: 'POST',
          body: JSON.stringify(clientData),
        });
      }
      loadClients();
      setShowModal(false);
      setEditingClient(null);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm(t.confirmDelete)) return;
    try {
      await apiCall(`/api/clients/${clientId}`, { method: 'DELETE' });
      loadClients();
    } catch (error) {
      alert(error.message);
    }
  };

  const filteredClients = clients.filter(c =>
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <User className="w-5 h-5 text-amber-600" />
          {t.title}
        </h2>
        <button
          onClick={() => { setEditingClient(null); setShowModal(true); }}
          className="flex items-center gap-2 bg-amber-700 text-white px-4 py-2 rounded-lg hover:bg-amber-800"
        >
          <Plus className="w-4 h-4" />
          {t.addClient}
        </button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder={t.search}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full max-w-md px-4 py-2 border rounded-lg"
        />
      </div>

      {/* Clients Table */}
      {loading ? (
        <div className="text-center py-8">
          <div className="w-8 h-8 border-4 border-amber-700 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <User className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          {t.noClients}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left p-3 font-medium">{t.name}</th>
                <th className="text-left p-3 font-medium">{t.contact}</th>
                <th className="text-left p-3 font-medium">{t.email}</th>
                <th className="text-left p-3 font-medium">{t.phone}</th>
                <th className="text-left p-3 font-medium">{t.vatNumber}</th>
                <th className="text-left p-3 font-medium">{t.city}</th>
                <th className="text-left p-3 font-medium">{t.actions}</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map(client => (
                <tr key={client.id} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium">{client.name}</td>
                  <td className="p-3 text-sm text-gray-600">{client.contact_name}</td>
                  <td className="p-3 text-sm">{client.email}</td>
                  <td className="p-3 text-sm">{client.phone}</td>
                  <td className="p-3 text-sm">{client.vat_number}</td>
                  <td className="p-3 text-sm">{client.city}</td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setEditingClient(client); setShowModal(true); }}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        title={t.edit}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(client.id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title={t.delete}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Client Modal */}
      {showModal && (
        <ClientModal
          client={editingClient}
          language={language}
          onClose={() => { setShowModal(false); setEditingClient(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default ClientsTab;
